import { useTranslation } from '@crehana/i18n';
import { LANGUAGES } from '@crehana/ts-types';
import { useRouter } from 'next/router';

import useFeatureFlag from '@/views/AdminDashboard/hooks/useFeatureFlag';
import useFlagOrganization from '@/views/AdminDashboard/hooks/useFlagOrganization';
import {
  CORP_LOWER_MENU_ROUTES,
  CORP_ROUTES,
  LEADER_ROUTES,
  LOWER_LEADER_ROUTES,
  LOWER_MENU_ROUTES,
  ROUTE_KEY,
  ROUTES,
  SKILL_IQ,
} from '@/views/AdminDashboard/routes';
import {
  HANA,
  LOWER_MENU_ROUTES as LowerTalentRoutes,
  ROUTES as TalentRoutes,
  UPPER_ROUTES as TalentUpperRoutes,
} from '@/views/AdminDashboard/routesTalent';

type useRoutesProps = {
  canUseInduction: boolean;
  canUseIntegrations: boolean;
  isCorporation: boolean;
  hasAdminModules: boolean;
};

const useRoutes = ({
  canUseIntegrations,
  isCorporation,
  hasAdminModules,
}: useRoutesProps) => {
  const { asPath } = useRouter();
  const { languageCode } = useTranslation();

  const { isActive: isTalent, isLoading } = useFlagOrganization({
    flag: 'talent-suite',
  });
  const { isActive: isOldSkillXPActive } = useFlagOrganization({
    flag: 'activate-oldest-skillxp',
  });
  const {
    isActive: isCrossPlatformFFActive,
    isLoading: isCrossPlatformLoading,
  } = useFlagOrganization({
    flag: 'talent-suite-cross-navigation',
  });

  const { isActive: isActiveIntegrations, isLoading: isLoadingIntegrations } =
    useFeatureFlag({ flag: 'enable_integrations_menu' });

  const isBrazil = languageCode === LANGUAGES.PT;

  const isTeamDashboard = asPath.includes('/lider/');

  let ORG_ROUTES = ROUTES;

  if (!canUseIntegrations && !isActiveIntegrations) {
    ORG_ROUTES = ORG_ROUTES.filter(
      route => route.key !== ROUTE_KEY.INTEGRATIONS,
    );
  }

  // If the useFeatureFlag 'deactivate-oldest-skillxp' is not in the features array
  // this means the organization has the old skill xp (skill iq for that matter) active
  // this will ber refactored in the near featrure, for any doubt contact @borisnieot or @yonatanrojas
  if (isOldSkillXPActive) {
    ORG_ROUTES = [...ORG_ROUTES, SKILL_IQ];
  }

  const brazilRoutes = ORG_ROUTES.filter(
    route => route.key !== ROUTE_KEY.SKILL_XP,
  );

  const organizationRoutes = isBrazil ? brazilRoutes : ORG_ROUTES;

  // eslint-disable-next-line no-nested-ternary
  const mainRoutes = isCorporation
    ? CORP_ROUTES
    : isTeamDashboard
      ? LEADER_ROUTES
      : organizationRoutes;

  // eslint-disable-next-line no-nested-ternary
  const secondaryRoutes = isCorporation
    ? CORP_LOWER_MENU_ROUTES
    : isTeamDashboard
      ? LOWER_LEADER_ROUTES
      : LOWER_MENU_ROUTES;

  const isLoadingRoutes =
    isLoadingIntegrations || isLoading || isCrossPlatformLoading;

  const isCrossPlatformActive = isCrossPlatformFFActive && hasAdminModules;

  const TalentRoutesAdapted = !hasAdminModules
    ? [HANA, ...TalentRoutes]
    : TalentRoutes;

  const TalentUpperRoutesAdapted = !hasAdminModules
    ? TalentUpperRoutes.filter(route => route.key !== ROUTE_KEY.HANA_AI)
    : TalentUpperRoutes;

  if (isTalent && !isTeamDashboard) {
    return {
      isBrazil,
      isTalent,
      isCrossPlatformActive,
      isLoadingRoutes: isLoadingRoutes,
      mainRoutes: TalentRoutesAdapted,
      secondaryRoutes: LowerTalentRoutes,
      upperRoutes: TalentUpperRoutesAdapted,
    };
  }

  return {
    isBrazil,
    isTalent,
    isCrossPlatformActive,
    isLoadingRoutes: isLoadingRoutes,
    mainRoutes,
    secondaryRoutes,
    upperRoutes: TalentUpperRoutes,
  };
};

export default useRoutes;
